import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Typography,
  Autocomplete,
} from '@mui/material';
import { Refresh, Login, Logout, HowToReg, EventBusy, BeachAccess, PendingActions } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { hrService, AttendanceRecord, AttendanceStatus, EmployeeListItem, TodayAttendanceSummary } from '../../services/hrService';

const statusTone: Record<AttendanceStatus, StatusTone> = {
  PRESENT: 'success',
  WFH: 'info',
  HALF_DAY: 'warning',
  ABSENT: 'error',
  ON_LEAVE: 'info',
  HOLIDAY: 'neutral',
  WEEK_OFF: 'neutral',
};

const statusLabel: Record<AttendanceStatus, string> = {
  PRESENT: 'Present',
  WFH: 'Work From Home',
  HALF_DAY: 'Half Day',
  ABSENT: 'Absent',
  ON_LEAVE: 'On Leave',
  HOLIDAY: 'Holiday',
  WEEK_OFF: 'Week Off',
};

const toIsoDate = (d: Date): string => d.toISOString().slice(0, 10);

const fmtTime = (v: string | null): string =>
  v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';

const fmtHours = (v: number | string | null): string => {
  if (v === null || v === undefined) return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? `${n.toFixed(2)} h` : '—';
};

const AttendancePage: React.FC = () => {
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 6);

  const [summary, setSummary] = useState<TodayAttendanceSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [from, setFrom] = useState(toIsoDate(weekAgo));
  const [to, setTo] = useState(toIsoDate(today));
  const [statusFilter, setStatusFilter] = useState<AttendanceStatus | ''>('');

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [actionEmployee, setActionEmployee] = useState<EmployeeListItem | null>(null);
  const [actionBusy, setActionBusy] = useState<'in' | 'out' | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadSummary = () => {
    setSummaryLoading(true);
    hrService
      .getTodaySummary()
      .then(setSummary)
      .catch(() => undefined)
      .finally(() => setSummaryLoading(false));
  };

  const loadRecords = () => {
    setLoading(true);
    setError(null);
    hrService
      .getAttendance({ from, to })
      .then((res) => setRecords(res.attendance))
      .catch((err: any) => setError(err.message || 'Failed to load attendance'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSummary();
    hrService
      .getEmployees({ limit: 100, status: 'ACTIVE' })
      .then((res) => setEmployees(res.employees))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const refreshAll = () => {
    loadSummary();
    loadRecords();
  };

  const filteredRecords = statusFilter ? records.filter((r) => r.status === statusFilter) : records;

  const handleCheckIn = async () => {
    if (!actionEmployee) return;
    setActionBusy('in');
    setActionMessage(null);
    try {
      await hrService.checkIn(actionEmployee.id);
      setActionMessage({ type: 'success', text: `Checked in ${actionEmployee.first_name}` });
      refreshAll();
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err.message || 'Check-in failed' });
    } finally {
      setActionBusy(null);
    }
  };

  const handleCheckOut = async () => {
    if (!actionEmployee) return;
    setActionBusy('out');
    setActionMessage(null);
    try {
      await hrService.checkOut(actionEmployee.id);
      setActionMessage({ type: 'success', text: `Checked out ${actionEmployee.first_name}` });
      refreshAll();
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err.message || 'Check-out failed' });
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Attendance"
        subtitle="Today's presence, check-in / check-out, and date-range history"
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={refreshAll} disabled={loading || summaryLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Present Today" value={summaryLoading ? null : summary?.byStatus.PRESENT ?? 0} icon={<HowToReg />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Absent Today" value={summaryLoading ? null : summary?.byStatus.ABSENT ?? 0} icon={<EventBusy />} color="#dc2626" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="On Leave Today" value={summaryLoading ? null : summary?.byStatus.ON_LEAVE ?? 0} icon={<BeachAccess />} color="#0891b2" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Not Marked" value={summaryLoading ? null : summary?.notMarked ?? 0} icon={<PendingActions />} color="#d97706" />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Mark Attendance
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            options={employees}
            value={actionEmployee}
            onChange={(_, v) => setActionEmployee(v)}
            getOptionLabel={(e) => `${e.first_name} ${e.last_name || ''} (${e.employee_code})`.trim()}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            sx={{ minWidth: 280 }}
            renderInput={(params) => <TextField {...params} label="Employee" placeholder="Search employee…" />}
          />
          <Button
            variant="contained"
            startIcon={<Login />}
            disabled={!actionEmployee || actionBusy !== null}
            onClick={handleCheckIn}
          >
            {actionBusy === 'in' ? 'Checking in…' : 'Check In'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Logout />}
            disabled={!actionEmployee || actionBusy !== null}
            onClick={handleCheckOut}
          >
            {actionBusy === 'out' ? 'Checking out…' : 'Check Out'}
          </Button>
        </Box>
        {actionMessage && (
          <Alert severity={actionMessage.type} sx={{ mt: 1.5 }} onClose={() => setActionMessage(null)}>
            {actionMessage.text}
          </Alert>
        )}
      </Paper>

      <FilterBar>
        <TextField
          size="small"
          label="From"
          type="date"
          value={from}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={to}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => setTo(e.target.value)}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as AttendanceStatus | '')}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All Status</MenuItem>
          {(Object.keys(statusLabel) as AttendanceStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              {statusLabel[s]}
            </MenuItem>
          ))}
        </TextField>
        <FilterSpacer />
        <Typography variant="body2" color="text.secondary">
          <strong>{filteredRecords.length}</strong> records
        </Typography>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Employee', 'Date', 'Check In', 'Check Out', 'Status', 'Worked', 'Overtime', 'Source'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <TableEmptyState title="No attendance records in this range" hint="Widen the date range or clear the status filter." />
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords.map((r) => (
                  <TableRow key={r.id} sx={bodyRowSx}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.employee_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {r.employee_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(r.attendance_date).toLocaleDateString('en-IN')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtTime(r.check_in)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtTime(r.check_out)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={statusLabel[r.status]} tone={statusTone[r.status]} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtHours(r.worked_hours)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtHours(r.overtime_hours)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.source}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default AttendancePage;
